import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

const VALID_CLAIMS = ["N", "NC", "NL", "NLC"];
const CLAIM_COLORS = {
    N: "#8884d8",    // Purple
    NC: "#82ca9d",    // Green
    NL: "#ffc658",    // Yellow
    NLC: "#ff8042",   // Orange
    Other: "#d3d3d3", // Light gray
};
const CLAIM_NAMES = {
    N: "Name",
    NC: "Name & Call Reason",
    NL: "Name & Logo",
    NLC: "Name, Logo & Call Reason",
    Other: "Other",
};

function initializeClaimsCounts() {
    const counts = {};
    VALID_CLAIMS.forEach(claim => counts[claim] = 0);
    counts.Other = 0;
    return counts;
}

function processCallsByEntity(data) {
    const entityCounts = {};
    data.forEach(call => {
        entityCounts[call.Entity] = (entityCounts[call.Entity] || 0) + 1;
    });

    return Object.entries(entityCounts).map(([entity, count]) => ({
        entity: entity,
        callCount: count
    })).sort((a, b) => b.callCount - a.callCount);
}

function processCallsByDisplayIdentity(data) {
    const displayIdentityStats = {};
    data.forEach(call => {
        // Use a composite key to uniquely identify the group by both Calling Entity and Display Identity
        const compositeKey = `${call.Entity} - ${call.Display_Identity}`;
        if (!displayIdentityStats[compositeKey]) {
            displayIdentityStats[compositeKey] = {
                callingEntity: call.Entity,
                displayIdentity: call.Display_Identity,
                totalCalls: 0,
                claimsPassedCounts: initializeClaimsCounts()
            };
        }
        displayIdentityStats[compositeKey].totalCalls++;
        const claim = call.Claims_Passed;
        if (VALID_CLAIMS.includes(claim)) {
            displayIdentityStats[compositeKey].claimsPassedCounts[claim]++;
        } else {
            displayIdentityStats[compositeKey].claimsPassedCounts.Other++;
        }
    });

    return Object.entries(displayIdentityStats).map(([compositeKey, stats]) => ({
        label: compositeKey, // This composite key will be used for the Y-axis label
        callingEntity: stats.callingEntity,
        displayIdentity: stats.displayIdentity,
        totalCalls: stats.totalCalls,
        claimsPassedCounts: stats.claimsPassedCounts
    })).sort((a, b) => b.totalCalls - a.totalCalls);
}

function processCallsByOriginationNumberForDisplayIdentity(data, drilldownSelection) {
    const originationNumberStats = {};
    // Filter by both Calling Entity and Display Identity for a precise drill-down
    const filteredData = data.filter(call => 
        call.Entity === drilldownSelection.callingEntity &&
        call.Display_Identity === drilldownSelection.displayIdentity
    );

    filteredData.forEach(call => {
        const origNum = call.Origination_Number;
        if (!originationNumberStats[origNum]) {
            originationNumberStats[origNum] = {
                totalCalls: 0,
                claimsPassedCounts: initializeClaimsCounts()
            };
        }
        originationNumberStats[origNum].totalCalls++;
        const claim = call.Claims_Passed;
        if (VALID_CLAIMS.includes(claim)) {
            originationNumberStats[origNum].claimsPassedCounts[claim]++;
        } else {
            originationNumberStats[origNum].claimsPassedCounts.Other++;
        }
    });

    return Object.entries(originationNumberStats).map(([origNum, stats]) => ({
        originationNumber: origNum,
        totalCalls: stats.totalCalls,
        claimsPassedCounts: stats.claimsPassedCounts
    })).sort((a, b) => b.totalCalls - a.totalCalls);
}

const CustomTooltipContent = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        let totalForTooltip = 0;
        const claimsDetails = [];

        if (data.claimsPassedCounts) { // For stacked bars (Display Identity, Origination Number)
            VALID_CLAIMS.forEach(claim => {
                if (data.claimsPassedCounts[claim] > 0) {
                    claimsDetails.push(<p key={claim} style={{ margin: '0 0 3px 0', color: CLAIM_COLORS[claim] }}>{`${CLAIM_NAMES[claim]}: ${data.claimsPassedCounts[claim]}`}</p>);
                }
                totalForTooltip += data.claimsPassedCounts[claim];
            });
            if (data.claimsPassedCounts.Other > 0) {
                 claimsDetails.push(<p key="Other" style={{ margin: '0 0 3px 0', color: CLAIM_COLORS.Other }}>{`${CLAIM_NAMES.Other}: ${data.claimsPassedCounts.Other}`}</p>);
                 totalForTooltip += data.claimsPassedCounts.Other;
            }
             return (
                <div className="custom-tooltip" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <p className="label" style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{`${label}`}</p>
                    {claimsDetails}
                    <p className="total" style={{ margin: '5px 0 0 0', fontWeight: 'bold', borderTop: '1px solid #eee', paddingTop: '5px' }}>{`Total Calls: ${data.totalCalls || totalForTooltip}`}</p>
                </div>
            );
        } else if (data.callCount !== undefined) { // For simple bar (Calls by Entity)
             return (
                <div className="custom-tooltip" style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <p className="label" style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{`${label}`}</p>
                    <p style={{ margin: 0 }}>{`Total Calls: ${data.callCount}`}</p>
                </div>
            );
        }
    }
    return null;
};

const CallPassCharts = ({ rawData, selectedDisplayIdentityForDrilldown, onDisplayIdentityClick }) => {
    if (!rawData || rawData.length === 0) {
        return <p style={{ textAlign: 'center', padding: '20px' }}>No data available to display charts.</p>;
    }

    const callsByEntityData = processCallsByEntity(rawData);
    const callsByDisplayIdentityData = processCallsByDisplayIdentity(rawData);
    let callsByOriginationData = [];
    if (selectedDisplayIdentityForDrilldown) {
        callsByOriginationData = processCallsByOriginationNumberForDisplayIdentity(rawData, selectedDisplayIdentityForDrilldown);
    }

    const chartMargin = { top: 5, right: 40, left: 20, bottom: 5 }; // Adjusted for LabelList
    const yAxisWidth = 350; // Increased width for longer Y-axis labels
    const barHeight = 30; // Height per bar
    const chartPadding = 60; // Padding for legend, axis names etc.

    const renderChart = (title, data, yAxisKey, isStacked, barClickCallback, stackIdPrefix = "stack") => {
        if (!data || data.length === 0) return <p>No data for {title.toLowerCase()}.</p>;
        const chartHeight = Math.max(150, data.length * (barHeight + 10) + chartPadding); // +10 for gap

        return (
            <div style={{ marginBottom: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.05)' }}>
                <h3 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>{title}</h3>
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart data={data} layout="vertical" margin={chartMargin} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey={yAxisKey} width={yAxisWidth} interval={0} tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltipContent />} wrapperStyle={{ zIndex: 1000 }}/>
                        <Legend />
                        {isStacked ? (
                            <>
                                {VALID_CLAIMS.map(claim => (
                                    <Bar key={claim} dataKey={`claimsPassedCounts.${claim}`} name={CLAIM_NAMES[claim]} stackId={`${stackIdPrefix}_${yAxisKey}`} fill={CLAIM_COLORS[claim]} onClick={barClickCallback ? (payload) => barClickCallback(payload) : undefined} barSize={barHeight}>
                                        {/* Optional: LabelList for individual segments if needed, but can be cluttered */}
                                    </Bar>
                                ))}
                                <Bar dataKey="claimsPassedCounts.Other" name={CLAIM_NAMES.Other} stackId={`${stackIdPrefix}_${yAxisKey}`} fill={CLAIM_COLORS.Other} onClick={barClickCallback ? (payload) => barClickCallback(payload) : undefined} barSize={barHeight} />
                                {/* Transparent bar for total label at the end of the stack */}
                                <Bar dataKey="totalCalls" stackId={`${stackIdPrefix}_total_label_${yAxisKey}`} fill="transparent" isAnimationActive={false} barSize={barHeight}>
                                    <LabelList dataKey="totalCalls" position="right" style={{ fill: 'black', fontSize: 12 }} />
                                </Bar>
                            </>
                        ) : (
                            <Bar dataKey="callCount" name="Total Calls" fill="#4A90E2" onClick={barClickCallback ? (payload) => barClickCallback(payload) : undefined} barSize={barHeight}>
                                <LabelList dataKey="callCount" position="right" style={{ fill: 'black', fontSize: 12 }} />
                            </Bar>
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div style={{ width: '100%', fontFamily: 'Arial, sans-serif' }}>
            {renderChart("Calls By Entity", callsByEntityData, "entity", false, null)}
            {renderChart("Calls By Display Identity", callsByDisplayIdentityData, "label", true, onDisplayIdentityClick, "dispId")}
            {selectedDisplayIdentityForDrilldown && callsByOriginationData.length > 0 && (
                renderChart(
                    `Calls By Origination Number for: ${selectedDisplayIdentityForDrilldown.label}`,
                    callsByOriginationData,
                    "originationNumber",
                    true,
                    null, // No click action from origination number bars in this design
                    "origNum"
                )
            )}
             {selectedDisplayIdentityForDrilldown && callsByOriginationData.length === 0 && (
                <p style={{ textAlign: 'center', padding: '20px' }}>No origination number data for {selectedDisplayIdentityForDrilldown.label}.</p>
            )}
        </div>
    );
};

export default CallPassCharts;